import { useState } from "react";
import axios from "axios";
import { X, Pencil, Download, Send, FileText, Save, Tags, CreditCard, MapPin, Building, Calendar, Check } from "lucide-react";
import { SearchableSelector, ClientSelector, InlinePriceEdit } from "./DevisShared";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUTS = ["devis", "réservé", "en_attente", "annulé"];
const STATUT_BADGE = {
  "devis": "bg-amber-100 text-amber-800",
  "réservé": "bg-green-100 text-green-800",
  "annulé": "bg-red-100 text-red-700",
  "en_attente": "bg-orange-100 text-orange-800",
};
const STATUT_LABEL = { "devis": "Devis", "réservé": "Réservé", "annulé": "Annulé", "en_attente": "En attente" };
const CHAMPAGNE_OPTS = ["", "Avec alcool", "Sans alcool", "Non"];
const PAYMENT_MODES = [
  { value: "especes", label: "Espèces" },
  { value: "cheque", label: "Chèque" },
  { value: "virement", label: "Virement" },
  { value: "cb", label: "Carte bancaire" },
];

export function DevisDetailModal({ devis: initialDevis, vehicles, themes, lieux, lieuxCeremonie, salles, clients, contacts, authHeaders, onClose, onUpdated, onRefresh }) {
  const [d, setD] = useState(initialDevis);
  const [tab, setTab] = useState("info");
  const [editStatut, setEditStatut] = useState(initialDevis.statut);
  const [invoices, setInvoices] = useState(initialDevis.invoices || []);
  const [reglements, setReglements] = useState(initialDevis.reglements || []);
  const [acompteAmt, setAcompteAmt] = useState(200);
  const [loadingInv, setLoadingInv] = useState(false);
  const [editForm, setEditForm] = useState({
    client_id: initialDevis.client_id || "",
    client_name: initialDevis.client_name, client_email: initialDevis.client_email,
    client_telephone: initialDevis.client_telephone, client_ville: initialDevis.client_ville,
    client_adresse: initialDevis.client_adresse || "", client_code_postal: initialDevis.client_code_postal || "",
    prenom_conjoint: initialDevis.prenom_conjoint || "",
    contact_preference: initialDevis.contact_preference || "", disponibilite_contact: initialDevis.disponibilite_contact || "",
    vehicle_id: initialDevis.vehicle_id, date_mariage: initialDevis.date_mariage,
    montant: initialDevis.montant,
    lieu_evenement: initialDevis.lieu_evenement || "",
    lieu_ceremonie: initialDevis.lieu_ceremonie || "",
    salle_reception: initialDevis.salle_reception || "",
    theme_decoration: initialDevis.theme_decoration || "",
    couleurs_decoration: initialDevis.couleurs_decoration || "", formule_decoration: initialDevis.formule_decoration || "",
    type_ceremonie: initialDevis.type_ceremonie || "", nombre_passagers: initialDevis.nombre_passagers || "",
    champagne: initialDevis.champagne || "",
    notes: initialDevis.notes || "",
    notes_internes: initialDevis.notes_internes || "",
    adresse_depart: initialDevis.adresse_depart || "",
    heure_depart: initialDevis.heure_depart || "",
    adresse_mairie: initialDevis.adresse_mairie || "",
    horaire_mairie: initialDevis.horaire_mairie || "",
    adresse_eglise: initialDevis.adresse_eglise || "",
    horaire_eglise: initialDevis.horaire_eglise || "",
    heure_fin_souhaitee: initialDevis.heure_fin_souhaitee || "", trajet_retour: initialDevis.trajet_retour || "",
    arrets_photos: initialDevis.arrets_photos || "", lieu_photos: initialDevis.lieu_photos || "",
    chauffeur_id: initialDevis.chauffeur_id || "",
    chauffeur_nom: initialDevis.chauffeur_nom || "",
    chauffeur_email: initialDevis.chauffeur_email || "",
    decoratrice_id: initialDevis.decoratrice_id || "",
    decoratrice_nom: initialDevis.decoratrice_nom || "",
    decoratrice_email: initialDevis.decoratrice_email || "",
  });
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState("");
  const [paymentForm, setPaymentForm] = useState({ montant: "", date: new Date().toISOString().slice(0,10), mode: "virement", reference: "", notes: "" });
  const [savingPayment, setSavingPayment] = useState(false);


  const totalRegle = reglements.reduce((s, r) => s + r.montant, 0);
  const reste = d.montant - totalRegle;
  const hasAcompte = invoices.some(i => i.type === "acompte");
  const hasSolde = invoices.some(i => i.type === "solde");
  const setE = (k, v) => setEditForm(f => ({ ...f, [k]: v }));
  const setP = (k, v) => setPaymentForm(f => ({ ...f, [k]: v }));

  const handleClientSelect = (client) => {
    setE("client_id", client.id);
    setE("client_name", client.nom);
    setE("client_email", client.email);
    setE("client_telephone", client.telephone);
    setE("client_ville", client.ville || "");
  };

  const handleContactSelect = (type, contact) => {
    if (type === "chauffeur") {
      setE("chauffeur_id", contact ? contact.id : "");
      setE("chauffeur_nom", contact ? contact.nom : "");
      setE("chauffeur_email", contact ? contact.email : "");
    } else {
      setE("decoratrice_id", contact ? contact.id : "");
      setE("decoratrice_nom", contact ? contact.nom : "");
      setE("decoratrice_email", contact ? contact.email : "");
    }
  };

  const chauffeurs = (contacts || []).filter(c => c.type === "chauffeur");
  const decoratrices = (contacts || []).filter(c => c.type === "decoratrice");

  const saveEdit = async () => {
    setSaving(true); setSaveError("");
    try {
      const updated = await axios.put(`${API}/devis/${d.id}`, editForm, { headers: authHeaders });
      setD(updated.data);
      setInvoices(updated.data.invoices || []);
      setReglements(updated.data.reglements || []);
      setTab("info");
      onUpdated();
    } catch (e) { setSaveError(e.response?.data?.detail || "Erreur de sauvegarde"); }
    finally { setSaving(false); }
  };

  const downloadCalendar = async () => {
    try {
      const res = await axios.get(`${API}/devis/${d.id}/ics`, { headers: authHeaders, responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "text/calendar" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `mariage-${(d.client_name || "").replace(/[^A-Za-z0-9_-]+/g, "-")}-${d.date_mariage}.ics`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 2000);
    } catch (e) { alert("Impossible de générer l'événement calendrier"); }
  };

  const saveStatut = async () => {
    const updated = await axios.put(`${API}/devis/${d.id}`, { statut: editStatut }, { headers: authHeaders });
    setD(updated.data); onUpdated();
  };

  const createInvoice = async (type) => {
    setLoadingInv(true);
    try {
      const res = await axios.post(`${API}/invoices`, { devis_id: d.id, type, montant: acompteAmt }, { headers: authHeaders });
      setInvoices(prev => [...prev, res.data]);
      onUpdated();
    } catch (e) { alert(e.response?.data?.detail || "Erreur"); }
    finally { setLoadingInv(false); }
  };

  const downloadInvoice = async (invId, numero) => {
    const res = await axios.get(`${API}/invoices/${invId}/pdf`, { headers: authHeaders, responseType: "blob" });
    const url = URL.createObjectURL(res.data);
    const a = document.createElement("a"); a.href = url; a.download = `facture-${numero}.pdf`; a.click();
  };

  const sendInvoice = async (invId, numero) => {
    await axios.post(`${API}/invoices/${invId}/send`, {}, { headers: authHeaders });
    alert(`Facture ${numero} envoyée à ${d.client_email}`);
    setInvoices(prev => prev.map(i => i.id === invId ? { ...i, sent: true } : i));
  };

  const deleteInvoice = async (invId) => {
    await axios.delete(`${API}/invoices/${invId}`, { headers: authHeaders });
    setInvoices(prev => prev.filter(i => i.id !== invId));
    onUpdated();
  };

  const addReglement = async () => {
    if (!paymentForm.montant || parseFloat(paymentForm.montant) <= 0) return;
    setSavingPayment(true);
    try {
      const res = await axios.post(`${API}/reglements`, {
        devis_id: d.id, montant: parseFloat(paymentForm.montant), date: paymentForm.date,
        mode: paymentForm.mode, reference: paymentForm.reference || null, notes: paymentForm.notes || null,
      }, { headers: authHeaders });
      setReglements(prev => [...prev, res.data]);
      setPaymentForm({ montant: "", date: new Date().toISOString().slice(0,10), mode: "virement", reference: "", notes: "" });
      onUpdated();
    } catch (e) { alert(e.response?.data?.detail || "Erreur"); }
    finally { setSavingPayment(false); }
  };

  const deleteReglement = async (regId) => {
    await axios.delete(`${API}/reglements/${regId}`, { headers: authHeaders });
    setReglements(prev => prev.filter(r => r.id !== regId));
    onUpdated();
  };

  const sendPaymentLink = async (type) => {
    const label = type === "acompte" ? "l'acompte (200 €)" : "le solde";
    if (!window.confirm(`Envoyer au client (${d.client_email}) un lien de paiement pour ${label} ?`)) return;
    try {
      const res = await axios.post(`${API}/devis/${d.id}/send-payment-link?payment_type=${type}`, {}, { headers: authHeaders });
      const url = res.data.url;
      const msg = res.data.email_sent === false
        ? `⚠️ Email simulé (SMTP non configuré). Lien généré (${res.data.amount} €).`
        : `✓ Lien de paiement envoyé à ${d.client_email} (${res.data.amount} €).`;
      if (window.confirm(`${msg}\n\nOK pour copier aussi le lien dans le presse-papier.`) && url && navigator.clipboard) {
        navigator.clipboard.writeText(url);
      }
    } catch (e) {
      alert(e.response?.data?.detail || "Impossible d'envoyer le lien de paiement");
    }
  };

  const TABS = [
    { id: "info", label: "Informations" },
    { id: "reglements", label: `Règlements (${reglements.length})`, icon: CreditCard },
    { id: "edit", label: "Modifier", icon: Pencil },
  ];

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white w-full max-w-2xl shadow-2xl max-h-[92vh] overflow-y-auto">
        <div className="flex items-center justify-between p-5 border-b border-gray-100">
          <div>
            <h2 className="font-bold text-gray-900">{d.client_name} — {d.numero}</h2>
            <span className={`text-xs font-semibold px-2 py-0.5 rounded-sm mt-1 inline-block ${STATUT_BADGE[d.statut]}`}>
              {STATUT_LABEL[d.statut]}
            </span>
          </div>
          <div className="flex items-center gap-3">
            <button onClick={downloadCalendar} data-testid="header-calendar-btn" title="Ajouter l'événement à mon calendrier"
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold border border-[#D4A843] text-[#D4A843] hover:bg-amber-50 transition-colors">
              <Calendar size={13} /> Ajouter à mon calendrier
            </button>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X size={18} /></button>
          </div>
        </div>

        <div className="flex border-b border-gray-100">
          {TABS.map(t => (
            <button key={t.id} onClick={() => setTab(t.id)} data-testid={`tab-${t.id}`}
              className={`flex items-center gap-1.5 px-5 py-3 text-sm font-semibold border-b-2 transition-colors ${
                tab === t.id ? "border-[#D4A843] text-[#D4A843]" : "border-transparent text-gray-500 hover:text-gray-700"}`}>
              {t.icon && <t.icon size={14} />} {t.label}
            </button>
          ))}
        </div>

        <div className="p-5">
          {/* EDIT MODE */}
          {tab === "edit" && (
            <div className="space-y-4">
              {saveError && <p className="text-red-500 text-sm bg-red-50 p-3">{saveError}</p>}
              <div className="space-y-4">
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1 uppercase tracking-wider">Client</label>
                  <ClientSelector value={editForm.client_id} onChange={handleClientSelect}
                    clients={clients} authHeaders={authHeaders} onClientCreated={onRefresh.fetchClients} />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="col-span-2">
                    <label className="block text-xs font-semibold text-gray-600 mb-1 uppercase tracking-wider">Véhicule</label>
                    <select value={editForm.vehicle_id} onChange={e => {
                        setE("vehicle_id", e.target.value);
                        const v = vehicles.find(v => v.id === e.target.value);
                        if (v) setE("montant", v.price);
                      }}
                      className="w-full border border-gray-200 px-3 py-2.5 text-sm focus:outline-none focus:border-[#D4A843]">
                      {vehicles.map(v => <option key={v.id} value={v.id}>{v.name} — {v.price}€</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1 uppercase tracking-wider">Montant (€)</label>
                    <input type="number" value={editForm.montant} onChange={e => setE("montant", parseInt(e.target.value) || 0)}
                      data-testid="edit-montant"
                      className="w-full border border-gray-200 px-3 py-2.5 text-sm focus:outline-none focus:border-[#D4A843] bg-amber-50 font-semibold" />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1 uppercase tracking-wider">Date mariage</label>
                    <input type="date" value={editForm.date_mariage} onChange={e => setE("date_mariage", e.target.value)}
                      className="w-full border border-gray-200 px-3 py-2.5 text-sm focus:outline-none focus:border-[#D4A843]" />
                  </div>
                  {[["client_email","Email","email"],["client_telephone","Téléphone","tel"],
                    ["client_ville","Ville","text"]].map(([key, label, type]) => (
                    <div key={key}>
                      <label className="block text-xs font-semibold text-gray-600 mb-1 uppercase tracking-wider">{label}</label>
                      <input type={type} value={editForm[key]} onChange={e => setE(key, e.target.value)}
                        className="w-full border border-gray-200 px-3 py-2.5 text-sm focus:outline-none focus:border-[#D4A843]" />
                    </div>
                  ))}
                  {[["prenom_conjoint","Prénom conjoint(e)"],["client_adresse","Adresse de facturation"],["client_code_postal","Code postal"],
                    ["contact_preference","Contact préféré"],["disponibilite_contact","Disponibilité contact"]].map(([key, label]) => (
                    <div key={key} className={key === "client_adresse" ? "col-span-2" : ""}>
                      <label className="block text-xs font-semibold text-gray-600 mb-1 uppercase tracking-wider">{label}</label>
                      <input type="text" value={editForm[key]} onChange={e => setE(key, e.target.value)}
                        className="w-full border border-gray-200 px-3 py-2.5 text-sm focus:outline-none focus:border-[#D4A843]" />
                    </div>
                  ))}
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1 uppercase tracking-wider">Lieu événement</label>
                    <SearchableSelector value={editForm.lieu_evenement} onChange={v => setE("lieu_evenement", v)}
                      items={lieux} placeholder="Sélectionner un lieu..." apiEndpoint="lieux"
                      authHeaders={authHeaders} onCreated={onRefresh.fetchLieux} icon={MapPin} />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1 uppercase tracking-wider">Lieu de la cérémonie</label>
                    <SearchableSelector value={editForm.lieu_ceremonie} onChange={v => setE("lieu_ceremonie", v)}
                      items={lieuxCeremonie} placeholder="Ville église / chapelle..." apiEndpoint="lieux-ceremonie"
                      authHeaders={authHeaders} onCreated={onRefresh.fetchLieuxCeremonie} icon={MapPin} />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1 uppercase tracking-wider">Salle de réception</label>
                    <SearchableSelector value={editForm.salle_reception} onChange={v => setE("salle_reception", v)}
                      items={salles} placeholder="Sélectionner une salle..." apiEndpoint="salles"
                      authHeaders={authHeaders} onCreated={onRefresh.fetchSalles} icon={Building} />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1 uppercase tracking-wider">Thème décoration</label>
                    <SearchableSelector value={editForm.theme_decoration} onChange={v => setE("theme_decoration", v)}
                      items={themes} placeholder="Sélectionner un thème..." apiEndpoint="themes"
                      authHeaders={authHeaders} onCreated={onRefresh.fetchThemes} icon={Tags} />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1 uppercase tracking-wider">Champagne</label>
                    <select value={editForm.champagne} onChange={e => setE("champagne", e.target.value)}
                      className="w-full border border-gray-200 px-3 py-2.5 text-sm focus:outline-none focus:border-[#D4A843]">
                      {CHAMPAGNE_OPTS.map(o => <option key={o}>{o}</option>)}
                    </select>
                  </div>
                  {[["type_ceremonie","Type de cérémonie"],["nombre_passagers","Nombre de passagers"],
                    ["formule_decoration","Formule décoration"],["couleurs_decoration","Couleurs / fleurs"]].map(([key, label]) => (
                    <div key={key}>
                      <label className="block text-xs font-semibold text-gray-600 mb-1 uppercase tracking-wider">{label}</label>
                      <input type="text" value={editForm[key]} onChange={e => setE(key, e.target.value)}
                        className="w-full border border-gray-200 px-3 py-2.5 text-sm focus:outline-none focus:border-[#D4A843]" />
                    </div>
                  ))}
                  <div className="col-span-2">
                    <label className="block text-xs font-semibold text-gray-600 mb-1 uppercase tracking-wider">Notes client</label>
                    <textarea value={editForm.notes} onChange={e => setE("notes", e.target.value)} rows={2}
                      className="w-full border border-gray-200 px-3 py-2.5 text-sm focus:outline-none focus:border-[#D4A843] resize-none" />
                  </div>
                  <div className="col-span-2">
                    <label className="block text-xs font-semibold text-gray-600 mb-1 uppercase tracking-wider">Notes internes (admin)</label>
                    <textarea value={editForm.notes_internes} onChange={e => setE("notes_internes", e.target.value)} rows={2}
                      placeholder="Notes visibles uniquement par l'admin..."
                      className="w-full border border-gray-200 px-3 py-2.5 text-sm focus:outline-none focus:border-[#D4A843] resize-none bg-yellow-50" />
                  </div>
                  <div className="col-span-2 border-t border-gray-100 pt-4">
                    <p className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-3">Équipe assignée</p>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-xs font-semibold text-gray-600 mb-1 uppercase tracking-wider">Chauffeur</label>
                        <select value={editForm.chauffeur_id} data-testid="edit-chauffeur-select"
                          onChange={e => { const c = chauffeurs.find(x => x.id === e.target.value); handleContactSelect("chauffeur", c || null); }}
                          className="w-full border border-gray-200 px-3 py-2.5 text-sm focus:outline-none focus:border-[#D4A843]">
                          <option value="">— Aucun —</option>
                          {chauffeurs.map(c => <option key={c.id} value={c.id}>{c.nom}</option>)}
                        </select>
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-gray-600 mb-1 uppercase tracking-wider">Décoratrice</label>
                        <select value={editForm.decoratrice_id} data-testid="edit-decoratrice-select"
                          onChange={e => { const c = decoratrices.find(x => x.id === e.target.value); handleContactSelect("decoratrice", c || null); }}
                          className="w-full border border-gray-200 px-3 py-2.5 text-sm focus:outline-none focus:border-[#D4A843]">
                          <option value="">— Aucune —</option>
                          {decoratrices.map(c => <option key={c.id} value={c.id}>{c.nom}</option>)}
                        </select>
                      </div>
                    </div>
                  </div>
                  <div className="col-span-2 border-t border-gray-100 pt-4">
                    <p className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-3">Logistique du jour J</p>
                    <div className="grid grid-cols-2 gap-4">
                      {[
                        ["adresse_depart","Adresse de départ","text","Ex: 12 Rue des Fleurs, Saint-Denis"],
                        ["heure_depart","Heure de départ","time",""],
                        ["adresse_mairie","Adresse mairie","text","Ex: Mairie de Saint-Denis"],
                        ["horaire_mairie","Horaire mairie","time",""],
                        ["adresse_eglise","Adresse église","text","Ex: Cathédrale Saint-Denis"],
                        ["horaire_eglise","Horaire église","time",""],
                        ["heure_fin_souhaitee","Heure de fin","time",""],
                        ["trajet_retour","Trajet retour","text","Oui, non ou à confirmer"],
                        ["arrets_photos","Arrêts photos","text","Oui, non ou à définir"],
                        ["lieu_photos","Lieu des photos","text","Plage, domaine..."],
                      ].map(([key, label, type, placeholder]) => (
                        <div key={key}>
                          <label className="block text-xs font-semibold text-gray-600 mb-1 uppercase tracking-wider">{label}</label>
                          <input type={type} value={editForm[key]} onChange={e => setE(key, e.target.value)}
                            placeholder={placeholder} data-testid={`edit-${key}`}
                            className="w-full border border-gray-200 px-3 py-2.5 text-sm focus:outline-none focus:border-[#D4A843]" />
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
              <div className="flex gap-3 justify-end pt-2 border-t border-gray-100">
                <button onClick={() => setTab("info")} className="px-5 py-2 border border-gray-200 text-sm font-semibold hover:bg-gray-50">Annuler</button>
                <button onClick={saveEdit} disabled={saving} data-testid="save-edit-btn"
                  className="flex items-center gap-2 px-6 py-2 bg-[#D4A843] text-white text-sm font-semibold hover:bg-[#c49933] disabled:opacity-60 transition-colors">
                  <Save size={14} /> {saving ? "Sauvegarde..." : "Enregistrer"}
                </button>
              </div>
            </div>
          )}

          {/* REGLEMENTS TAB */}
          {tab === "reglements" && (
            <div className="space-y-5">
              <div className="grid grid-cols-3 gap-3">
                <div className="bg-gray-50 p-3 text-center">
                  <p className="text-xs text-gray-400 mb-1">Total devis</p>
                  <p className="text-lg font-bold text-gray-900">{d.montant}€</p>
                </div>
                <div className="bg-green-50 p-3 text-center">
                  <p className="text-xs text-gray-400 mb-1">Réglé</p>
                  <p className={`text-lg font-bold ${totalRegle > 0 ? "text-green-600" : "text-gray-300"}`}>{totalRegle > 0 ? `${totalRegle}€` : "—"}</p>
                </div>
                <div className={`p-3 text-center ${reste <= 0 ? "bg-green-50" : "bg-orange-50"}`}>
                  <p className="text-xs text-gray-400 mb-1">Reste</p>
                  <p className={`text-lg font-bold ${reste <= 0 ? "text-green-600" : "text-orange-500"}`}>{reste <= 0 ? "Soldé" : `${reste}€`}</p>
                </div>
              </div>
              {d.statut === "réservé" && reste > 0 && (
                <div className="bg-[#faf6ec] border border-[#e8d9a8] p-4 space-y-3 rounded">
                  <p className="text-xs font-semibold text-gray-600 uppercase tracking-wider">Lien de paiement en ligne (Stripe)</p>
                  <p className="text-xs text-gray-500">Envoie au client un lien de paiement sécurisé par email (copie à l'admin). Disponible uniquement pour une réservation confirmée.</p>
                  <div className="flex flex-wrap gap-2">
                    <button onClick={() => sendPaymentLink("acompte")} data-testid="send-acompte-link"
                      className="flex items-center gap-2 px-4 py-2 bg-[#D4A843] text-white text-sm font-semibold hover:brightness-95 rounded">
                      <CreditCard size={14} /> Envoyer lien acompte (200 €)
                    </button>
                    <button onClick={() => sendPaymentLink("solde")} data-testid="send-solde-link"
                      className="flex items-center gap-2 px-4 py-2 border border-[#D4A843] text-[#a9821f] text-sm font-semibold hover:bg-[#faf6ec] rounded">
                      <CreditCard size={14} /> Envoyer lien solde ({reste} €)
                    </button>
                  </div>
                </div>
              )}
              <div>
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">Règlements enregistrés</p>
                {reglements.length === 0 ? (
                  <p className="text-sm text-gray-400 italic mb-3">Aucun règlement enregistré</p>
                ) : (
                  <div className="space-y-2 mb-4">
                    {reglements.map(r => (
                      <div key={r.id} className="flex items-center justify-between p-3 border border-gray-100 rounded bg-gray-50">
                        <div>
                          <div className="flex items-center gap-2">
                            <CreditCard size={13} className="text-gray-400" />
                            <span className="font-semibold text-green-600">{r.montant}€</span>
                            <span className="px-1.5 py-0.5 text-xs rounded font-medium bg-gray-200 text-gray-700">
                              {PAYMENT_MODES.find(m => m.value === r.mode)?.label || r.mode}
                            </span>
                          </div>
                          <p className="text-xs text-gray-400 mt-0.5">{r.date} {r.reference && `• Réf: ${r.reference}`}</p>
                          {r.notes && <p className="text-xs text-gray-500 mt-1">{r.notes}</p>}
                        </div>
                        <button onClick={() => deleteReglement(r.id)} title="Supprimer" className="p-1.5 text-gray-400 hover:text-red-500"><X size={13} /></button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              {reste > 0 && (
                <div className="bg-blue-50 p-4 space-y-3">
                  <p className="text-xs font-semibold text-gray-600 uppercase tracking-wider">Ajouter un règlement</p>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs text-gray-600 mb-1">Montant (€) *</label>
                      <input type="number" value={paymentForm.montant} onChange={e => setP("montant", e.target.value)}
                        max={reste} step="0.01" placeholder={`Max: ${reste}€`} data-testid="payment-montant"
                        className="w-full border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:border-blue-400 bg-white" />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-600 mb-1">Date *</label>
                      <input type="date" value={paymentForm.date} onChange={e => setP("date", e.target.value)} data-testid="payment-date"
                        className="w-full border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:border-blue-400 bg-white" />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-600 mb-1">Mode *</label>
                      <select value={paymentForm.mode} onChange={e => setP("mode", e.target.value)} data-testid="payment-mode"
                        className="w-full border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:border-blue-400 bg-white">
                        {PAYMENT_MODES.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs text-gray-600 mb-1">Référence</label>
                      <input type="text" value={paymentForm.reference} onChange={e => setP("reference", e.target.value)}
                        placeholder="N° chèque, virement..." data-testid="payment-reference"
                        className="w-full border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:border-blue-400 bg-white" />
                    </div>
                    <div className="col-span-2">
                      <label className="block text-xs text-gray-600 mb-1">Notes</label>
                      <input type="text" value={paymentForm.notes} onChange={e => setP("notes", e.target.value)}
                        placeholder="Commentaire..." data-testid="payment-notes"
                        className="w-full border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:border-blue-400 bg-white" />
                    </div>
                  </div>
                  <button onClick={addReglement} disabled={savingPayment || !paymentForm.montant} data-testid="add-payment-btn"
                    className="w-full py-2.5 bg-green-600 text-white text-sm font-semibold hover:bg-green-700 disabled:opacity-60">
                    {savingPayment ? "..." : "Enregistrer le règlement"}
                  </button>
                </div>
              )}
              {reste <= 0 && <p className="text-center text-green-600 text-sm font-semibold py-3 bg-green-50">Prestation entièrement réglée</p>}
            </div>
          )}

          {/* INFO MODE */}
          {tab === "info" && (
            <div className="space-y-5">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-1">Client</p>
                  <p className="font-semibold text-gray-900">{d.client_name}</p>
                  <p className="text-gray-500">{d.client_email}</p>
                  <p className="text-gray-400 text-xs">{d.client_telephone}</p>
                </div>
                <div>
                  <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-1">Événement</p>
                  <p className="font-semibold text-[#D4A843]">{d.date_mariage}</p>
                  <p className="text-gray-500">{d.client_ville}</p>
                  {d.lieu_evenement && <p className="text-gray-400 text-xs">{d.lieu_evenement}</p>}
                  {d.salle_reception && <p className="text-gray-400 text-xs">Salle: {d.salle_reception}</p>}
                  <button onClick={downloadCalendar} data-testid="download-calendar-btn"
                    className="mt-2 flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold border border-[#D4A843] text-[#D4A843] hover:bg-amber-50 transition-colors">
                    <Calendar size={12} /> Ajouter au calendrier
                  </button>
                </div>
                <div>
                  <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-1">Véhicule</p>
                  <p className="font-semibold text-gray-900">{d.vehicle_name}</p>
                  <p className="text-[#D4A843] font-bold">{d.montant}€</p>
                </div>
                <div>
                  <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-1">Options</p>
                  {d.theme_decoration && <p className="text-sm text-gray-600">Thème : <span className="font-medium">{d.theme_decoration}</span></p>}
                  {d.champagne && <p className="text-sm text-gray-500">Champagne : {d.champagne}</p>}
                  {d.notes && <p className="text-xs text-gray-400 mt-1">{d.notes}</p>}
                </div>
              </div>
              {d.notes_internes && (
                <div className="bg-yellow-50 p-3 border border-yellow-200">
                  <p className="text-xs font-semibold text-yellow-700 mb-1">Notes internes</p>
                  <p className="text-sm text-yellow-800">{d.notes_internes}</p>
                </div>
              )}
              {(d.client_adresse || d.type_ceremonie || d.formule_decoration || d.contact_preference) && (
                <div className="bg-amber-50 p-4 border border-amber-100">
                  <p className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-3">Formulaire client complet</p>
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    {d.prenom_conjoint && <div><p className="text-xs text-gray-400">Conjoint(e)</p><p className="font-medium">{d.prenom_conjoint}</p></div>}
                    {d.client_adresse && <div><p className="text-xs text-gray-400">Facturation</p><p className="font-medium">{d.client_adresse}, {d.client_code_postal} {d.client_ville}</p></div>}
                    {d.contact_preference && <div><p className="text-xs text-gray-400">Contact</p><p className="font-medium">{d.contact_preference}, {d.disponibilite_contact}</p></div>}
                    {d.type_ceremonie && <div><p className="text-xs text-gray-400">Cérémonie / passagers</p><p className="font-medium">{d.type_ceremonie}, {d.nombre_passagers}</p></div>}
                    {d.formule_decoration && <div><p className="text-xs text-gray-400">Décoration</p><p className="font-medium">{d.formule_decoration}, {d.couleurs_decoration}</p></div>}
                    {d.heure_fin_souhaitee && <div><p className="text-xs text-gray-400">Fin / retour</p><p className="font-medium">{d.heure_fin_souhaitee}, retour {d.trajet_retour}</p></div>}
                    {d.arrets_photos && <div><p className="text-xs text-gray-400">Photos</p><p className="font-medium">{d.arrets_photos}{d.lieu_photos ? `, ${d.lieu_photos}` : ""}</p></div>}
                    {d.consentement_at && <div><p className="text-xs text-gray-400">Consentement</p><p className="font-medium text-green-700">Validé le {d.consentement_at.slice(0,10)}</p></div>}
                  </div>
                </div>
              )}
              {(d.chauffeur_nom || d.decoratrice_nom || d.adresse_depart || d.adresse_mairie) && (
                <div className="bg-gray-50 p-4 border border-gray-100 space-y-3">
                  <p className="text-xs font-bold text-gray-500 uppercase tracking-wider">Logistique & Équipe</p>
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    {d.chauffeur_nom && (
                      <div>
                        <p className="text-xs text-gray-400">Chauffeur</p>
                        <p className="font-semibold text-blue-700">{d.chauffeur_nom}</p>
                        <p className="text-xs text-gray-400">{d.chauffeur_email}</p>
                      </div>
                    )}
                    {d.decoratrice_nom && (
                      <div>
                        <p className="text-xs text-gray-400">Décoratrice</p>
                        <p className="font-semibold text-pink-700">{d.decoratrice_nom}</p>
                        <p className="text-xs text-gray-400">{d.decoratrice_email}</p>
                      </div>
                    )}
                    {d.adresse_depart && (
                      <div>
                        <p className="text-xs text-gray-400">Départ</p>
                        <p className="font-medium">{d.adresse_depart}</p>
                        {d.heure_depart && <p className="text-xs text-[#D4A843] font-bold">{d.heure_depart}</p>}
                      </div>
                    )}
                    {d.adresse_mairie && (
                      <div>
                        <p className="text-xs text-gray-400">Mairie</p>
                        <p className="font-medium">{d.adresse_mairie}</p>
                        {d.horaire_mairie && <p className="text-xs text-[#D4A843] font-bold">{d.horaire_mairie}</p>}
                      </div>
                    )}
                    {d.adresse_eglise && (
                      <div>
                        <p className="text-xs text-gray-400">Église</p>
                        <p className="font-medium">{d.adresse_eglise}</p>
                        {d.horaire_eglise && <p className="text-xs text-[#D4A843] font-bold">{d.horaire_eglise}</p>}
                      </div>
                    )}
                  </div>
                  {d.confirmed_at && (
                    <p className="text-xs text-green-600 font-semibold"><Check size={12} className="inline mr-1" />Infos confirmées le {d.confirmed_at.slice(0,10)}</p>
                  )}
                </div>
              )}
              <div className="grid grid-cols-3 gap-3">
                <div className="bg-gray-50 p-3 text-center group relative">
                  <p className="text-xs text-gray-400 mb-1">Total</p>
                  <InlinePriceEdit
                    montant={d.montant}
                    onSave={async (newPrice) => {
                      const updated = await axios.put(`${API}/devis/${d.id}`, { montant: newPrice }, { headers: authHeaders });
                      setD(updated.data); onUpdated();
                    }}
                  />
                </div>
                <div className="bg-green-50 p-3 text-center">
                  <p className="text-xs text-gray-400 mb-1">Réglé</p>
                  <p className={`text-lg font-bold ${totalRegle > 0 ? "text-green-600" : "text-gray-300"}`}>{totalRegle > 0 ? `${totalRegle}€` : "—"}</p>
                </div>
                <div className={`p-3 text-center ${reste <= 0 ? "bg-green-50" : "bg-orange-50"}`}>
                  <p className="text-xs text-gray-400 mb-1">Reste</p>
                  <p className={`text-lg font-bold ${reste <= 0 ? "text-green-600" : "text-orange-500"}`}>{reste <= 0 ? "Soldé" : `${reste}€`}</p>
                </div>
              </div>
              <div className="flex items-center justify-between py-3 border-y border-gray-100">
                <div className="flex items-center gap-3">
                  <span className="text-sm font-semibold text-gray-700">Statut :</span>
                  <span className={`px-2 py-1 text-xs font-semibold rounded-sm ${STATUT_BADGE[d.statut]}`}>{STATUT_LABEL[d.statut]}</span>
                  <select value={editStatut} onChange={e => setEditStatut(e.target.value)} data-testid="statut-select"
                    className="border border-gray-200 text-sm px-2 py-1 focus:outline-none focus:border-[#D4A843]">
                    {STATUTS.map(s => <option key={s} value={s}>{STATUT_LABEL[s]}</option>)}
                  </select>
                </div>
                <button onClick={saveStatut} data-testid="save-statut-btn"
                  className="px-4 py-2 bg-[#1B2E4B] text-white text-sm font-semibold hover:bg-[#162440] transition-colors">Appliquer</button>
              </div>
              <div>
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">Factures</p>
                {invoices.length === 0 && <p className="text-sm text-gray-400 italic mb-3">Aucune facture générée</p>}
                <div className="space-y-2 mb-4">
                  {invoices.map(inv => (
                    <div key={inv.id} className="flex items-center justify-between p-3 border border-gray-100 rounded">
                      <div>
                        <div className="flex items-center gap-2">
                          <FileText size={13} className="text-gray-400" />
                          <span className="font-semibold text-sm">{inv.numero}</span>
                          <span className={`px-1.5 py-0.5 text-xs rounded font-medium ${inv.type === "acompte" ? "bg-blue-100 text-blue-700" : "bg-purple-100 text-purple-700"}`}>
                            {inv.type === "acompte" ? "Acompte" : "Solde"}
                          </span>
                          {inv.sent && <span className="text-xs text-green-600">✓ Envoyée</span>}
                        </div>
                        <p className="text-xs text-gray-400 mt-0.5">{inv.montant}€ — {inv.created_at?.slice(0, 10)}</p>
                      </div>
                      <div className="flex gap-1">
                        <button onClick={() => downloadInvoice(inv.id, inv.numero)} title="Télécharger" className="p-1.5 text-gray-400 hover:text-blue-600"><Download size={13} /></button>
                        <button onClick={() => sendInvoice(inv.id, inv.numero)} title="Envoyer" className="p-1.5 text-gray-400 hover:text-green-600"><Send size={13} /></button>
                        <button onClick={() => deleteInvoice(inv.id)} title="Supprimer" className="p-1.5 text-gray-400 hover:text-red-500"><X size={13} /></button>
                      </div>
                    </div>
                  ))}
                </div>
                {!hasAcompte && reste > 0 && (
                  <div className="flex items-end gap-3 bg-blue-50 p-3">
                    <div className="flex-1">
                      <label className="text-xs font-semibold text-gray-600 uppercase tracking-wider block mb-1">Montant acompte (€)</label>
                      <input type="number" value={acompteAmt} onChange={e => setAcompteAmt(parseInt(e.target.value) || 200)}
                        min={1} max={d.montant - 1} data-testid="acompte-amount-input"
                        className="w-full border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:border-blue-400 bg-white" />
                    </div>
                    <button onClick={() => createInvoice("acompte")} disabled={loadingInv} data-testid="gen-acompte-btn"
                      className="px-4 py-2 bg-blue-600 text-white text-sm font-semibold hover:bg-blue-700 disabled:opacity-60 whitespace-nowrap">
                      Générer acompte
                    </button>
                  </div>
                )}
                {hasAcompte && !hasSolde && reste > 0 && (
                  <button onClick={() => createInvoice("solde")} disabled={loadingInv} data-testid="gen-solde-btn"
                    className="w-full py-3 bg-purple-600 text-white text-sm font-semibold hover:bg-purple-700 disabled:opacity-60">
                    Générer facture de solde — {reste}€
                  </button>
                )}
                {reste <= 0 && invoices.length > 0 && (
                  <p className="text-center text-green-600 text-sm font-semibold py-2 bg-green-50">Prestation entièrement facturée</p>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
